import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Map as MapIcon, User as UserIcon } from "lucide-react";

export default function TerritoriesPage() {
  const [states, setStates] = useState([]);
  const [users, setUsers] = useState([]);
  const [territories, setTerritories] = useState([]);
  const [activeTeam, setActiveTeam] = useState("sales");

  const reload = useCallback(async () => {
    const [s, u, t] = await Promise.all([
      api.get("/meta/states"),
      api.get("/users"),
      api.get("/territories"),
    ]);
    setStates(s.data);
    setUsers(u.data);
    setTerritories(t.data);
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const eligibleUsers = users.filter((u) => (u.teams || []).includes(activeTeam));
  const map = {};
  territories.filter((t) => t.team === activeTeam).forEach((t) => { map[t.state] = t.user_id; });

  const setOwner = async (state, userId) => {
    try {
      const payload = { team: activeTeam, state, user_id: userId || null };
      await api.put("/territories", payload);
      toast.success(userId ? "Territory updated" : "Territory cleared");
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    }
  };

  const nameFor = (uid) => users.find((u) => u.id === uid)?.name || "";

  return (
    <div className="p-8 space-y-6">
      <div>
        <div className="data-label mb-1">Auto-routing</div>
        <h1 className="font-heading font-black text-4xl tracking-tight flex items-center gap-2">
          <MapIcon className="w-8 h-8 text-[#4F46E5]" /> Territories
        </h1>
        <p className="text-sm text-[#52525B] mt-2 max-w-2xl">
          Map each Indian state to the Sales / Service rep who should own new leads and complaints from that state.
          When a form is submitted with that state, the record auto-assigns to the mapped person.
          Unmapped states fall to the shared queue (visible to the team lead and Admin).
        </p>
      </div>

      <div className="flex items-center gap-2">
        {["sales", "service"].map((t) => (
          <Button
            key={t}
            variant={activeTeam === t ? "default" : "outline"}
            className={activeTeam === t ? "bg-[#4F46E5] hover:bg-[#4338CA] text-white" : ""}
            onClick={() => setActiveTeam(t)}
            data-testid={`territory-tab-${t}`}
          >
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </Button>
        ))}
        <div className="text-xs text-[#71717A] ml-3">
          {eligibleUsers.length} eligible {activeTeam} team member{eligibleUsers.length === 1 ? "" : "s"}
        </div>
      </div>

      <div className="card-shell overflow-hidden">
        <table className="data-table">
          <thead>
            <tr><th style={{ width: "40%" }}>State</th><th>Owner</th><th style={{ width: "180px" }}>Action</th></tr>
          </thead>
          <tbody data-testid="territories-tbody">
            {states.map((s) => (
              <tr key={s} data-testid={`territory-row-${s}`}>
                <td className="font-medium">{s}</td>
                <td>
                  {map[s] ? (
                    <span className="inline-flex items-center gap-1 text-sm">
                      <UserIcon className="w-3.5 h-3.5 text-[#4F46E5]" />
                      {nameFor(map[s]) || <span className="font-mono text-xs text-[#71717A]">{map[s]}</span>}
                    </span>
                  ) : (
                    <span className="text-xs text-[#A1A1AA] italic">Unassigned (falls to team lead / admin)</span>
                  )}
                </td>
                <td>
                  <Select
                    value={map[s] || "__none"}
                    onValueChange={(v) => setOwner(s, v === "__none" ? "" : v)}
                  >
                    <SelectTrigger data-testid={`assign-${s}`}>
                      <SelectValue placeholder="Assign to…" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none">— Unassigned —</SelectItem>
                      {eligibleUsers.map((u) => (
                        <SelectItem key={u.id} value={u.id}>{u.name} · {u.email}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
